import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface OnboardingContextType {
  isOnboardingComplete: boolean;
  showOnboarding: boolean;
  completeOnboarding: (data: Record<string, unknown>) => void;
  skipOnboarding: () => void;
  resetOnboarding: () => void;
  isTourActive: boolean;
  isTourComplete: boolean;
  startTour: () => void;
  completeTour: () => void;
  skipTour: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}

interface OnboardingProviderProps {
  children: React.ReactNode;
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isTourActive, setIsTourActive] = useState(false);
  const [isTourComplete, setIsTourComplete] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem('onboarding_completed');
    const hasCompletedOnboarding = completed === 'true';
    const tourCompleted = localStorage.getItem('product_tour_completed') === 'true';
    const tourSkipped = localStorage.getItem('product_tour_skipped') === 'true';

    setIsOnboardingComplete(hasCompletedOnboarding);
    setIsTourComplete(tourCompleted || tourSkipped);

    if (!hasCompletedOnboarding) {
      const timer = setTimeout(() => {
        setShowOnboarding(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  const completeOnboarding = (data: Record<string, unknown>) => {
    localStorage.setItem('onboarding_completed', 'true');
    localStorage.setItem('onboarding_data', JSON.stringify(data));

    setIsOnboardingComplete(true);
    setShowOnboarding(false);

    const tourDone = localStorage.getItem('product_tour_completed') === 'true';
    const tourSkippedBefore = localStorage.getItem('product_tour_skipped') === 'true';
    if (!tourDone && !tourSkippedBefore) {
      setTimeout(() => setIsTourActive(true), 500);
    }
  };

  const skipOnboarding = () => {
    localStorage.setItem('onboarding_completed', 'true');
    localStorage.setItem('onboarding_skipped', 'true');

    setIsOnboardingComplete(true);
    setShowOnboarding(false);
  };

  const resetOnboarding = () => {
    localStorage.removeItem('onboarding_completed');
    localStorage.removeItem('onboarding_data');
    localStorage.removeItem('onboarding_skipped');
    localStorage.removeItem('product_tour_completed');
    localStorage.removeItem('product_tour_skipped');

    setIsOnboardingComplete(false);
    setIsTourComplete(false);
    setIsTourActive(false);
    setShowOnboarding(true);
  };

  const startTour = useCallback(() => {
    setIsTourActive(true);
    setIsTourComplete(false);
  }, []);

  const completeTour = useCallback(() => {
    localStorage.setItem('product_tour_completed', 'true');
    setIsTourActive(false);
    setIsTourComplete(true);
  }, []);

  const skipTour = useCallback(() => {
    localStorage.setItem('product_tour_skipped', 'true');
    setIsTourActive(false);
    setIsTourComplete(true);
  }, []);

  return (
    <OnboardingContext.Provider value={{
      isOnboardingComplete,
      showOnboarding,
      completeOnboarding,
      skipOnboarding,
      resetOnboarding,
      isTourActive,
      isTourComplete,
      startTour,
      completeTour,
      skipTour,
    }}>
      {children}
    </OnboardingContext.Provider>
  );
}